import React from 'react'
import { Card, CardBody, Row, Col, Badge, Alert, Table } from 'reactstrap'
import PageWrapper from '../../../components/Common/PageWrapper'

const ApiStatus = () => {
  document.title = 'API Status & Features - Aurobil'

  const features = [
    {
      name: 'Running Balance',
      status: 'active',
      released: '2026-03-31',
      impact: '~50% faster ledger queries',
      description: 'Transaction ledger now includes pre-calculated running balance for each entry',
    },
    {
      name: 'Audit Logging',
      status: 'active',
      released: '2026-03-31',
      impact: 'Full compliance tracking',
      description: 'All account changes tracked with user, timestamp, and change details',
    },
    {
      name: 'API Versioning',
      status: 'active',
      released: '2026-03-31',
      impact: 'Non-breaking updates possible',
      description: 'Structured versioning for backward compatibility and planned evolution',
    },
  ]

  const getStatusBadge = (status) => {
    const config = {
      active: { color: 'success', label: 'Active' },
      coming: { color: 'info', label: 'Coming Soon' },
      deprecated: { color: 'warning', label: 'Deprecated' },
      retired: { color: 'danger', label: 'Retired' },
    }
    const c = config[status] || { color: 'secondary', label: 'Unknown' }
    return <Badge color={c.color}>{c.label}</Badge>
  }

  return (
    <PageWrapper title="API Status & Features" breadcrumbItem="API Status">
      {/* Header Info */}
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex align-items-center gap-3 mb-3">
            <div className="bg-primary rounded-circle p-3" style={{ width: 50, height: 50 }}>
              <i className="mdi mdi-api text-white" style={{ fontSize: 24 }} />
            </div>
            <div>
              <h5 className="mb-1">API Versioning</h5>
              <p className="text-muted small mb-0">
                Aurobil now supports versioned API endpoints for stable, non-breaking updates
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Version Status Cards */}
      <Row className="mb-4 g-3">
        {/* V1 Card */}
        <Col lg={6}>
          <Card className="border-success-subtle">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0 fw-semibold">
                  <i className="mdi mdi-check-circle text-success me-2" />
                  API v1 (Current)
                </h6>
                <Badge color="success">Active</Badge>
              </div>
              <div className="ps-4">
                <div className="small mb-2">
                  <code className="bg-light px-2 py-1 rounded">Base URL: /api/v1/*</code>
                </div>
                <ul className="small text-muted ps-3 mb-0">
                  <li>Released: 2026-03-31</li>
                  <li>Status: Actively maintained</li>
                  <li>Sunset: 2027-12-31</li>
                </ul>
                <div className="mt-3">
                  <span className="small fw-semibold text-success">
                    <i className="mdi mdi-check me-1" />
                    Recommended for all new clients
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Legacy Card */}
        <Col lg={6}>
          <Card className="border-warning-subtle">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0 fw-semibold">
                  <i className="mdi mdi-alert-circle text-warning me-2" />
                  API Legacy
                </h6>
                <Badge color="warning">Deprecated</Badge>
              </div>
              <div className="ps-4">
                <div className="small mb-2">
                  <code className="bg-light px-2 py-1 rounded">Base URL: /api/*</code>
                </div>
                <ul className="small text-muted ps-3 mb-0">
                  <li>Status: Deprecated</li>
                  <li>Support until: 2026-12-31</li>
                  <li className="text-danger fw-semibold">Action required before sunset</li>
                </ul>
                <div className="mt-3">
                  <span className="small text-danger">
                    <i className="mdi mdi-alert me-1" />
                    Migrate to /api/v1/* before 2026-12-31
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Feature Status */}
      <Card className="mb-4">
        <CardBody>
          <h6 className="fw-semibold mb-3">
            <i className="mdi mdi-star-circle me-2 text-info" />
            Feature Status
          </h6>

          <div className="table-responsive">
            <Table hover className="small mb-0">
              <thead>
                <tr className="bg-light">
                  <th>Feature</th>
                  <th>Status</th>
                  <th>Release Date</th>
                  <th>Impact</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, idx) => (
                  <tr key={idx}>
                    <td className="fw-semibold">{feature.name}</td>
                    <td>{getStatusBadge(feature.status)}</td>
                    <td className="text-muted">{feature.released}</td>
                    <td>
                      <Badge color="light" className="text-dark">
                        <i className="mdi mdi-lightning-bolt me-1" />
                        {feature.impact}
                      </Badge>
                    </td>
                    <td className="text-muted">{feature.description}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* Migration Guide */}
      <Card className="mb-4">
        <CardBody>
          <h6 className="fw-semibold mb-3">
            <i className="mdi mdi-file-document-outline me-2 text-primary" />
            Migration Guide for API v1
          </h6>

          <Alert color="info" className="mb-3">
            <strong>All endpoints are now available at /api/v1/*</strong>
            <p className="small mb-0 mt-2">
              Both /api/* (legacy) and /api/v1/* (new) will work until 2026-12-31. Plan your migration
              accordingly.
            </p>
          </Alert>

          <div className="bg-light p-3 rounded mb-3">
            <h6 className="small fw-semibold mb-2">Before (Legacy):</h6>
            <code className="text-danger">GET /api/account-transactions</code>
            <code className="d-block text-danger">POST /api/account-expenses</code>
          </div>

          <div className="bg-light p-3 rounded mb-3">
            <h6 className="small fw-semibold mb-2">After (Recommended):</h6>
            <code className="text-success">GET /api/v1/account-transactions</code>
            <code className="d-block text-success">POST /api/v1/account-expenses</code>
          </div>

          <div className="border-start border-3 border-warning ps-3">
            <p className="small mb-1 fw-semibold text-warning">
              <i className="mdi mdi-alert-circle me-1" />
              No response format changes in v1
            </p>
            <p className="small text-muted mb-0">
              Request/response structures remain identical. Simply replace the base URL path.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* What's New */}
      <Card>
        <CardBody>
          <h6 className="fw-semibold mb-3">
            <i className="mdi mdi-new-box me-2 text-success" />
            What's New in v1 (2026-03-31)
          </h6>

          <Row className="g-3">
            <Col md={6}>
              <div className="d-flex gap-3">
                <div>
                  <div className="bg-success-subtle rounded p-2">
                    <i className="mdi mdi-chart-line text-success" style={{ fontSize: 20 }} />
                  </div>
                </div>
                <div>
                  <h6 className="mb-1 small fw-semibold">Running Balance</h6>
                  <p className="small text-muted mb-0">
                    Transactions now include pre-calculated running balance. See account balance at each
                    point in history instantly without recalculation.
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="d-flex gap-3">
                <div>
                  <div className="bg-success-subtle rounded p-2">
                    <i className="mdi mdi-history text-success" style={{ fontSize: 20 }} />
                  </div>
                </div>
                <div>
                  <h6 className="mb-1 small fw-semibold">Audit Logging</h6>
                  <p className="small text-muted mb-0">
                    Full audit trail of all account entity changes. Track who changed what, when, and why.
                    Perfect for compliance and debugging.
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="d-flex gap-3">
                <div>
                  <div className="bg-success-subtle rounded p-2">
                    <i className="mdi mdi-api text-success" style={{ fontSize: 20 }} />
                  </div>
                </div>
                <div>
                  <h6 className="mb-1 small fw-semibold">API Versioning</h6>
                  <p className="small text-muted mb-0">
                    Non-breaking versioned endpoints. Future API updates will be released as v2, v3, etc.
                    without disrupting existing integrations.
                  </p>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="d-flex gap-3">
                <div>
                  <div className="bg-info-subtle rounded p-2">
                    <i className="mdi mdi-clock-fast text-info" style={{ fontSize: 20 }} />
                  </div>
                </div>
                <div>
                  <h6 className="mb-1 small fw-semibold">Performance</h6>
                  <p className="small text-muted mb-0">
                    Running balance improvements result in ~50% faster transaction ledger queries. Better
                    performance across the board.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Footer Info */}
      <div className="mt-4 text-center text-muted small">
        <p>For detailed API documentation, visit the backend docs or contact API support.</p>
      </div>
    </PageWrapper>
  )
}

export default ApiStatus
